import { jsx as _jsx } from "react/jsx-runtime";
/**
 * Minimal Markdown renderer for the legal documents store (BP-22). Covers exactly what the stored
 * documents use — ## / ### headings, **bold**, *italic*, bullet lists, pipe tables, paragraphs and
 * links as plain text — with tokens-only styling and NO dependency. Never renders HTML from the
 * input: every piece of text lands as escaped React text nodes, so stored content cannot inject
 * markup. Not a general-purpose renderer; documents are trusted, versioned reference data.
 */
const H2 = { fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 600, letterSpacing: '-.01em', margin: '18px 0 7px' };
const H3 = { fontFamily: 'var(--font-display)', fontSize: 13.5, fontWeight: 600, margin: '14px 0 5px' };
const P = { margin: '0 0 9px', lineHeight: 1.65, color: 'var(--ink-soft)' };
const LI = { margin: '0 0 5px', lineHeight: 1.6, color: 'var(--ink-soft)' };
const TABLE = { width: '100%', borderCollapse: 'collapse', margin: '0 0 12px', fontSize: '0.96em' };
// break-word (not 'anywhere') so words never split mid-word unless a single token exceeds the cell.
const TD = { border: '1px solid var(--line)', padding: '7px 10px', verticalAlign: 'top', lineHeight: 1.55, color: 'var(--ink-soft)', overflowWrap: 'break-word' };
// Short label cells (the doc-control tables' first column: Provider, Version, Acceptance…) never wrap.
const TD_LABEL = { ...TD, whiteSpace: 'nowrap', width: '1%' };
/** Inline pass: **bold** and *italic* only, everything else as literal text. */
function inline(text, keyBase) {
    const out = [];
    let rest = text;
    let i = 0;
    while (rest.length > 0) {
        const bold = rest.indexOf('**');
        if (bold === -1) {
            out.push(rest);
            break;
        }
        const end = rest.indexOf('**', bold + 2);
        if (end === -1) {
            out.push(rest);
            break;
        }
        if (bold > 0)
            out.push(rest.slice(0, bold));
        out.push(_jsx("b", { style: { color: 'var(--ink)', fontWeight: 600 }, children: rest.slice(bold + 2, end) }, `${keyBase}-b${i}`));
        rest = rest.slice(end + 2);
        i += 1;
    }
    return out;
}
function isTableRow(line) {
    const t = line.trim();
    return t.startsWith('|') && t.endsWith('|');
}
function splitRow(line) {
    const t = line.trim();
    return t.slice(1, -1).split('|').map((c) => c.trim());
}
function isDividerRow(cells) {
    return cells.every((c) => /^:?-{3,}:?$/.test(c) || c === '');
}
export function Markdown({ body }) {
    const lines = body.split('\n');
    const blocks = [];
    let para = [];
    let list = [];
    let table = [];
    let k = 0;
    const flushPara = () => {
        if (para.length === 0)
            return;
        const text = para.join(' ');
        blocks.push(_jsx("p", { style: P, children: inline(text, `p${k}`) }, `p${k++}`));
        para = [];
    };
    const flushList = () => {
        if (list.length === 0)
            return;
        blocks.push(_jsx("ul", { style: { margin: '0 0 10px', paddingLeft: 20 }, children: list.map((item, i) => _jsx("li", { style: LI, children: inline(item, `u${k}-${i}`) }, i)) }, `u${k++}`));
        list = [];
    };
    const flushTable = () => {
        if (table.length === 0)
            return;
        const rows = table.filter((cells) => !isDividerRow(cells));
        blocks.push(_jsx("table", { style: TABLE, children: _jsx("tbody", { children: rows.map((cells, ri) => (_jsx("tr", { children: cells.map((c, ci) => (_jsx("td", { style: ci === 0 && c.replace(/\*\*/g, '').length <= 24 ? TD_LABEL : TD, children: inline(c, `t${k}-${ri}-${ci}`) }, ci))) }, ri))) }) }, `t${k++}`));
        table = [];
    };
    const flushAll = () => { flushPara(); flushList(); flushTable(); };
    for (const raw of lines) {
        const line = raw ?? '';
        const trimmed = line.trim();
        if (isTableRow(line)) {
            flushPara();
            flushList();
            table.push(splitRow(line));
            continue;
        }
        flushTable();
        if (trimmed === '') {
            flushAll();
            continue;
        }
        if (trimmed.startsWith('### ')) {
            flushAll();
            blocks.push(_jsx("h4", { style: H3, children: inline(trimmed.slice(4), `h${k}`) }, `h${k++}`));
            continue;
        }
        if (trimmed.startsWith('## ')) {
            flushAll();
            blocks.push(_jsx("h3", { style: H2, children: inline(trimmed.slice(3), `h${k}`) }, `h${k++}`));
            continue;
        }
        if (trimmed.startsWith('# ')) {
            flushAll();
            blocks.push(_jsx("h3", { style: H2, children: inline(trimmed.slice(2), `h${k}`) }, `h${k++}`));
            continue;
        }
        if (trimmed.startsWith('- ')) {
            flushPara();
            flushTable();
            list.push(trimmed.slice(2));
            continue;
        }
        para.push(trimmed);
    }
    flushAll();
    return _jsx("div", { style: { fontSize: 12.5 }, children: blocks });
}
/** Trigger a browser download of the document as a Markdown file. */
export function downloadMarkdown(filename, body) {
    const blob = new Blob([body], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}
//# sourceMappingURL=Markdown.js.map